import type { LogConfig, LogEvent, Logger } from '../../types/logging/core';

export interface GatewayWorker {
    send<T>(payload: T): void

    /* eslint-disable  @typescript-eslint/no-explicit-any */
    on(type: string, callback: (message: any) => void): void

    onmessage: ((this: GatewayWorker, message: MessageEvent) => Promise<void> | void) | null
    onerror: ((this: GatewayWorker, error: ErrorEvent) => void) | null

    exit(code?: number): void

    findParameter(name: string): string | null | undefined

    streamLogs(level: LogConfig['level']): Promise<{ getLogger: (name: string) => Logger }>

    importModule<T>(moduleName: string): Promise<T>
}

const worker: GatewayWorker = {
    send: (): void => { },
    on: (): void => { },
    onmessage: null,
    onerror: null,
    exit: (): void => { },
    findParameter(name: string): string | null | undefined {
        let value = new URLSearchParams(globalThis.location?.search).get(name);
        if (!value) {
            const arg =  globalThis.process?.argv
                .filter(a => a.startsWith('--'))
                .map((arg) => {
                    const strings = arg.slice('--'.length).split('=');
                    return [strings[0], strings[1]];
                })
                .find(([k, _v]) => k === name);
            if (arg) {
                [, value] = arg;
            }
        }
        return value;
    },
    async streamLogs(level: LogConfig['level'] = 'trace'): Promise<{ getLogger: (name: string) => Logger }> {
        const appender = (e: LogEvent) => {
            worker.send({
                log: {
                    time: e.time.getTime(),
                    level: e.level,
                    name: e.name,
                    message: e.message,
                    data: e.data
                }
            });
        }
        const logging = await import('../logging/core.ts');
        logging.configure({ level, appender });
        return logging;
    },
    async importModule<T>(moduleName: string): Promise<T> {
        return await import(moduleName);
    }
}

if (typeof self !== 'undefined' && typeof postMessage === 'function' && typeof addEventListener === 'function') {
    worker.on = (event, callback) => {
        addEventListener(event, (message) => {
            callback(message);
        })
    }
    worker.send = <T>(payload: T) => {
        postMessage(payload)
    };
    if (typeof close === 'function') {
        worker.exit = close;
    }
} else if (typeof process !== 'undefined') {
    (globalThis as { self?: unknown }).self = worker;
    worker.on = process.on.bind(process);

    const send = process.send?.bind(process);
    if (send) { // probably just loading the file from non worker context
        worker.send = <T>(payload: T) => {
            send(JSON.stringify({type: 'message', data: payload}));
        };
    }

    worker.on('disconnect', () => {
        process.exit(1);
    });

    process.on('unhandledRejection', (reason: unknown) => {
        if (send) {
            send(JSON.stringify({type: 'error', message: `Unhandled Rejection: ${reason instanceof Error ? reason.message : String(reason)}`}) );
        }
    });
    process.on('uncaughtException', (error) => {
        if (send) {
            send(JSON.stringify({type: 'error', message: `Uncaught Error: ${error.message}`}));
        }
    });
    worker.exit = process.exit.bind(process);
}

worker.on('message', (message: MessageEvent) => {
    if (worker.onmessage) {
        worker.onmessage(message);
    }
});

worker.on('error', (error: ErrorEvent) => {
    if (worker.onerror) {
        worker.onerror(error);
    }
});

export default worker;
